import { useState, useRef, useCallback } from "react";
import { locationManagementService } from "../services/locationManagementService";

type ZipLookupFieldSetter = (
  _field: string,
  _value: unknown,
  _shouldValidate?: boolean
) => void;

type ZipLookupTouchedSetter = (
  _field: string,
  _isTouched?: boolean,
  _shouldValidate?: boolean
) => void;

type ZipLookupLocation = {
  state?: string;
  state_code?: string;
  stateCode?: string;
  state_name?: string;
  city?: string;
  city_name?: string;
};

type ZipLookupResponse = {
  data?:
    | {
        locationData?: ZipLookupLocation[];
        data?: ZipLookupLocation[];
      }
    | ZipLookupLocation[];
};

type ZipLookupSuccessPayload = {
  state: string;
  city: string;
};

const getFirstPresentValue = (...values: Array<string | null | undefined>) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

export const useZipLookup = (
  country: string | string[] | undefined,
  setFieldValue: ZipLookupFieldSetter,
  setFieldTouched?: ZipLookupTouchedSetter,
  onLookupSuccess?: (_location: ZipLookupSuccessPayload) => void
) => {
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupSuccess, setLookupSuccess] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleZipChange = useCallback(
    (zipValue: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);

      const resetLocationFields = (hasLookupError = false) => {
        setFieldValue("zip_lookup_error", hasLookupError, hasLookupError);
        setFieldValue("state", "");
        setFieldValue("city", "");

        if (hasLookupError) {
          setFieldTouched?.("state", true, false);
          setFieldTouched?.("city", true, false);
        } else {
          setFieldTouched?.("state", false, false);
          setFieldTouched?.("city", false, false);
        }
      };

      resetLocationFields();
      setLookupSuccess(false);

      if (!zipValue || !country) return;

      const countryCode = Array.isArray(country) ? country[0] : country;
      const requiredZipLength = countryCode === "CA" ? 6 : 5;
      if (zipValue.length !== requiredZipLength) return;

      timerRef.current = setTimeout(async () => {
        setIsLookingUp(true);
        try {
          const response = (await locationManagementService.lookupZipCode(
            zipValue,
            countryCode
          )) as ZipLookupResponse;
          const responseData = response.data;
          const locationData =
            (Array.isArray(responseData)
              ? responseData[0]
              : responseData?.locationData?.[0] || responseData?.data?.[0]) ||
            null;
          if (!locationData) {
            resetLocationFields(true);
            return;
          }
          const state =
            getFirstPresentValue(
              locationData.state,
              locationData.state_code,
              locationData.stateCode,
              locationData.state_name
            ) || "";
          const city =
            getFirstPresentValue(locationData.city, locationData.city_name) ||
            "";
          if (!state || !city) {
            resetLocationFields(true);
            return;
          }
          onLookupSuccess?.({ state, city });
          setFieldValue("zip_lookup_error", false, false);
          setFieldValue("state", state);
          setFieldValue("city", city);
          setLookupSuccess(true);
        } catch {
          resetLocationFields(true);
        } finally {
          setIsLookingUp(false);
        }
      }, 500);
    },
    [country, onLookupSuccess, setFieldValue, setFieldTouched]
  );

  return { handleZipChange, isLookingUp, lookupSuccess };
};
